import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card } from 'primereact/card';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import LoadingComponent from '../loadingComponent';
import Service from '../../services';
import { baseUrlAdmin } from '../../store/apiConstants';
import './ChannelLearning.scss';

/**
 * Channel Learning Dashboard
 * Shows which communication channels work best for different students
 */
class ChannelLearning extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            channelStats: {},
            byProfession: {},
            bestContactTimes: {},
            chartData: null
        };
        this.service = new Service();
        this.toast = React.createRef();
    }

    componentDidMount() {
        this.fetchChannelEffectiveness();
        this.fetchByProfession();
        this.fetchBestContactTimes();
    }

    fetchChannelEffectiveness = async () => {
        this.setState({ loading: true });
        try {
            const academicYear = localStorage.getItem('userAcademicYear');
            const url = `${baseUrlAdmin}/fee-decision/learning/channel-effectiveness?academicYear=${academicYear}`;
            
            const response = await this.service.get(url, true);
            
            if (response && response.status) {
                const { channelStats, totalStudents, studentsWithLearning } = response.res.data;
                this.setState({ channelStats, totalStudents, studentsWithLearning });
                this.prepareChartData(channelStats);
            }
        } catch (error) {
            console.error('Error fetching channel effectiveness:', error);
            this.showToast('error', 'Error', 'Failed to fetch channel effectiveness');
        } finally {
            this.setState({ loading: false });
        }
    }

    fetchByProfession = async () => {
        try {
            const academicYear = localStorage.getItem('userAcademicYear');
            const url = `${baseUrlAdmin}/fee-decision/learning/channel-preferences/by-profession?academicYear=${academicYear}`;
            
            const response = await this.service.get(url, true);
            
            if (response && response.status) {
                this.setState({ byProfession: response.res.data || {} });
            }
        } catch (error) {
            console.error('Error fetching by profession:', error);
        }
    }

    fetchBestContactTimes = async () => {
        try {
            const academicYear = localStorage.getItem('userAcademicYear');
            const url = `${baseUrlAdmin}/fee-decision/learning/best-contact-times?academicYear=${academicYear}`;
            
            const response = await this.service.get(url, true);
            
            if (response && response.status) {
                this.setState({ bestContactTimes: response.res.data || {} });
            }
        } catch (error) {
            console.error('Error fetching best contact times:', error);
        }
    }

    prepareChartData = (channelStats) => {
        const channels = Object.keys(channelStats);
        const colors = {
            'WHATSAPP': '#25D366',
            'SMS': '#FFA500',
            'CALL': '#4285F4',
            'EMAIL': '#EA4335'
        };
        
        const chartData = {
            labels: channels,
            datasets: [
                {
                    label: 'Effectiveness Rate',
                    data: channels.map(ch => channelStats[ch]?.effectivenessRate || 0),
                    backgroundColor: channels.map(ch => colors[ch] || '#607D8B')
                }
            ]
        };
        
        this.setState({ chartData });
    }

    showToast = (severity, summary, detail) => {
        this.toast.current?.show({
            severity: severity,
            summary: summary,
            detail: detail,
            life: 3000
        });
    }

    renderChannelCards() {
        const { channelStats } = this.state;
        
        const channelIcons = {
            'WHATSAPP': 'pi-whatsapp',
            'SMS': 'pi-comment',
            'CALL': 'pi-phone',
            'EMAIL': 'pi-envelope'
        };
        
        const channelColors = {
            'WHATSAPP': 'whatsapp',
            'SMS': 'sms',
            'CALL': 'call',
            'EMAIL': 'email'
        };
        
        return (
            <div className="channel-cards">
                {Object.keys(channelStats).map(channel => {
                    const stats = channelStats[channel];
                    return (
                        <Card key={channel} className={`channel-card ${channelColors[channel]}`}>
                            <div className="card-content">
                                <div className="card-header">
                                    <i className={`pi ${channelIcons[channel]}`}></i>
                                    <h3>{channel}</h3>
                                </div>
                                <div className="card-stats">
                                    <div className="stat-item">
                                        <div className="stat-value">{stats.effectivenessRate}%</div>
                                        <div className="stat-label">Effectiveness</div>
                                    </div>
                                    <div className="stat-item">
                                        <div className="stat-value">{stats.paymentRate}%</div>
                                        <div className="stat-label">Payment Rate</div>
                                    </div>
                                    <div className="stat-item">
                                        <div className="stat-value">{stats.total}</div>
                                        <div className="stat-label">Students Tracked</div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    );
                })}
            </div>
        );
    }

    renderProfessionPreferences() {
        const { byProfession } = this.state;
        
        if (Object.keys(byProfession).length === 0) {
            return <div className="no-data">No profession preference data available</div>;
        }
        
        return (
            <div className="profession-preferences">
                {Object.keys(byProfession).map(profession => (
                    <Card key={profession} className="profession-card">
                        <h4>{profession === 'UNKNOWN' ? 'Not Set' : profession}</h4>
                        <div className="preference-bars">
                            {byProfession[profession].map((pref, idx) => {
                                const total = byProfession[profession].reduce((sum, p) => sum + (p.count || 0), 0);
                                const percentage = total > 0 ? Math.round((pref.count / total) * 100) : 0;
                                
                                return (
                                    <div key={idx} className="preference-bar">
                                        <div className="bar-label">
                                            <span>{pref.channel}</span>
                                            <span>
                                                {pref.count} students ({percentage}%)
                                                {pref.effectivenessRate !== undefined && 
                                                    <span className="effectiveness-badge"> • {pref.effectivenessRate}% effective</span>
                                                }
                                            </span>
                                        </div>
                                        <div className="bar-track">
                                            <div 
                                                className={`bar-fill ${pref.channel.toLowerCase()}`}
                                                style={{ width: `${percentage}%` }}
                                            ></div>
                                        </div>
                                        {(pref.totalInteractions || pref.effectiveInteractions) && (
                                            <div className="bar-details">
                                                {pref.totalInteractions && <span>{pref.totalInteractions} interactions</span>}
                                                {pref.effectiveInteractions && <span> • {pref.effectiveInteractions} effective</span>}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </Card>
                ))}
            </div>
        );
    }

    renderBestContactTimes() {
        const { bestContactTimes } = this.state;
        
        if (!bestContactTimes.peakHours || bestContactTimes.peakHours.length === 0) {
            return <div className="no-data">No contact time data available</div>;
        }
        
        return (
            <div className="contact-times">
                <div className="peak-hours">
                    <h4>Peak Contact Hours</h4>
                    {bestContactTimes.peakHours.map((peak, idx) => (
                        <div key={idx} className="peak-item">
                            <div className="peak-rank">#{idx + 1}</div>
                            <div className="peak-details">
                                <div className="peak-time">{peak.label}</div>
                                <div className="peak-count">{peak.count} successful contacts</div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="time-chart">
                    <h4>Contact Success by Hour</h4>
                    <Chart 
                        type="line" 
                        data={{
                            labels: Object.keys(bestContactTimes.hourDistribution || {}).map(h => `${h}:00`),
                            datasets: [{
                                label: 'Success Count',
                                data: Object.values(bestContactTimes.hourDistribution || {}),
                                borderColor: '#42A5F5',
                                backgroundColor: 'rgba(66, 165, 245, 0.2)',
                                tension: 0.4
                            }]
                        }}
                        options={{ maintainAspectRatio: false }}
                        height="200px"
                    />
                </div>
            </div>
        );
    }

    render() {
        const { loading, chartData, totalStudents, studentsWithLearning } = this.state;
        
        if (loading && !chartData) {
            return <LoadingComponent />;
        }
        
        return (
            <div className="channel-learning-container">
                <Toast ref={this.toast} position="bottom-right" />
                
                <div className="page-header">
                    <h2>Channel Learning Dashboard</h2>
                    <p>Discover which communication channels work best for different parents</p>
                    <div className="header-stats">
                        <Tag severity="info" value={`${totalStudents || 0} Students`} />
                        <Tag severity="success" value={`${studentsWithLearning || 0} With Channel Preference`} />
                    </div>
                </div>
                
                {this.renderChannelCards()}
                
                {chartData && (
                    <Card className="chart-card">
                        <h3>Channel Effectiveness Comparison</h3>
                        <Chart type="bar" data={chartData} options={{ maintainAspectRatio: false }} height="300px" />
                    </Card>
                )}
                
                <Card className="profession-card-container">
                    <h3>Preferred Channels by Profession</h3>
                    {this.renderProfessionPreferences()}
                </Card>
                
                <Card className="contact-times-card">
                    <h3>Best Contact Times</h3>
                    {this.renderBestContactTimes()}
                </Card>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    authenticated: state.authenticated
});

export default connect(mapStateToProps)(ChannelLearning);
